import React from "react";
import makeStyles from "@mui/styles/makeStyles";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableFooter from "@mui/material/TableFooter";
import TablePagination from "@mui/material/TablePagination";
import Paper from "@mui/material/Paper";

import { Box, Button, useMediaQuery, useTheme } from "@mui/material";
import { getColumnWidths, ExercisesTableRow } from "./ExercisesTableRow";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import {
  BodypartType,
  MovementPatternType,
  ExerciseAssetDto,
  ExerciseAssetStatus,
} from "@growth-machine-llc/stridist-api-client";
import { EditorProgramContext } from "../new-editor/hooks";
import ExerciseAssetsService from "../../services/ExerciseAssetsService";
import { ExerciseLibraryDrawer } from "./ExerciseLibraryDrawer";
import ExerciseLibraryDrawerContext from "../../contexts/ExericeLibraryDrawerContext";
import {
  ALL_OPTIONS,
  useExerciseListFilterContext,
} from "../../contexts/ExerciseLibraryFilterContext";
import { useDebounce } from "../../hooks/useDebounce";
import { BodypartFilter } from "./filters/BodypartFilter";
import { MovementPatternFilter } from "./filters/MovementPatternFilter";
import { NameSortButton } from "./filters/NameSortButton";
import { ExercisesTableMobileView } from "./ExercisesTableCardsView";
import {
  ExerciseLibraryCardSkeleton,
  ExerciseLibraryTableSkeleton,
} from "../loading/ExerciseLibrarySkeletons";
import clsx from "clsx";
import { CategoryType } from "./ManageLibraryExercise";
import ExerciseTagsService from "../../services/ExerciseTagsService";
import { TagsFilters, NONE_TAG_ID } from "./filters/TagsFilters";
import { JourneyAnchors } from "../../constants";
import { Info } from "lucide-react";
import MinimizedTooltip from "../tooltip/MinimizedTooltip";

const useStyles = makeStyles((theme) => ({
  root: {
    borderRadius: theme.spacing(1.5),
    padding: 1,
    boxShadow: theme.shadows[4],
    marginBottom: theme.spacing(3),
    [theme.breakpoints.down("md")]: {
      boxShadow: "none",
    },
  },

  tabs: {
    "& span": {
      fontWeight: "bold",
      fontSize: 14,
      textTransform: "uppercase",
    },
    borderBottom: "1px solid",
    borderBottomColor: theme.palette.selected.main,
  },

  container: {
    margin: 0,
    containerType: "inline-size",

    [theme.breakpoints.up("md")]: {
      margin: theme.spacing(1, 2),
    },
  },

  searchContainer: {
    display: "flex",
    alignItems: "flex-start",
    width: "100%",
    justifyContent: "space-between",
    gap: theme.spacing(5),
    flexWrap: "wrap",
    [theme.breakpoints.down("md")]: {
      gap: theme.spacing(2),
    },
  },

  searchInput: {
    "& input": {
      fontSize: 15,
      padding: theme.spacing(2),
      minWidth: theme.spacing(50),
    },
    marginBottom: theme.spacing(2),
  },

  summary: {
    margin: theme.spacing(1.5, 0),
    fontWeight: 500,
    fontSize: 16,
  },

  table: {
    "& th": {
      fontWeight: "bold",
      fontSize: 15,
    },

    "& td": {
      fontWeight: 500,
      fontSize: 14,
    },

    "& tfoot td": {
      borderBottom: "none",
    },
  },

  createButton: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    "&:hover": {
      backgroundColor: theme.palette.primary.dark,
    },
  },

  search: {
    backgroundColor: theme.palette.background.paper,
  },

  nameSortButton: {
    padding: 0,
  },

  infoIcon: {
    marginLeft: theme.spacing(1.2),
  },

  suggestedBy: {
    fontWeight: "bold",
    fontSize: 15,
    color: theme.palette.text.primary,
    cursor: "default",
    "&:hover": {
      backgroundColor: "transparent",
    },
  },
}));

export interface ExercisesTableProps {
  status: ExerciseAssetStatus;
}

export const getBodypartTypes = (filters: Record<BodypartType, boolean>) =>
  Object.entries(filters)
    .filter(([, enabled]) => enabled)
    .map(([type]) => type) as BodypartType[];

export const getMovementPatternTypes = (
  filters: Record<MovementPatternType, boolean>,
) => {
  return Object.entries(filters)
    .filter(([, enabled]) => enabled)
    .map(([type]) => type) as MovementPatternType[];
};

export const areAllSelected = <T extends CategoryType>(
  selected: T[],
  allOptions: T[],
) => {
  return (
    selected?.length === allOptions?.length &&
    allOptions.every((item) => selected.includes(item))
  );
};

export const allBodyparts = Object.values(BodypartType);
export const allMovementPatterns = Object.values(MovementPatternType);

export const allBodypartsSelected = (selected: BodypartType[]) =>
  areAllSelected(selected, allBodyparts);

export const allMovementPatternsSelected = (selected: MovementPatternType[]) =>
  areAllSelected(selected, allMovementPatterns);

export const COACH_EXERCISE_LIBRARY_ASSETS_QUERY_KEY =
  "exercise-library-assets";

export const ALL_EXERCISE_TAGS_QUERY_KEY = "all-exercise-tags";

//TODO_STR-2139: Set rows per page based on screen size
const DEFAULT_ROWS_PER_PAGE = 10;
const DEFAULT_PAGE = 1;

export function ExercisesTable(props: ExercisesTableProps) {
  const { status } = props;
  const s = useStyles();

  const isPendingTab = status === ExerciseAssetStatus.PENDING;
  const isPublishedTab = status === ExerciseAssetStatus.PUBLISHED;
  const isArchivedTab = status === ExerciseAssetStatus.ARCHIVED;

  const disableChangeExercise =
    status === ExerciseAssetStatus.ARCHIVED ||
    status === ExerciseAssetStatus.PENDING;

  const columnWidths = getColumnWidths(isPendingTab, isPublishedTab);

  const { breakpoints } = useTheme();
  const mdUp = useMediaQuery(breakpoints.up("md"));

  const [page, setPage] = React.useState(DEFAULT_PAGE);
  const [rowsPerPage, setRowsPerPage] = React.useState(DEFAULT_ROWS_PER_PAGE);

  const createButtonContext = React.useContext(ExerciseLibraryDrawerContext);
  const {
    query,
    sortKey,
    bodyparts: contextBodyparts,
    movementPatterns: localMovementPatterns,
    exerciseType,
    tagIds: localStorageTagIds,
    handleBodypartChange,
    handleMovementPatternChange,
    handleSortKeyChange,
    handleTagIdsChange,
  } = useExerciseListFilterContext();

  const { data: exerciseTags } = useQuery({
    queryKey: [ALL_EXERCISE_TAGS_QUERY_KEY],
    queryFn: () => {
      return ExerciseTagsService.getAllExerciseTags();
    },
  });

  const debounceQuery = useDebounce(query, 500);

  const { exerciseLibraryDrawerOpen, setExerciseLibraryDrawerOpen } =
    createButtonContext;

  const [exerciseAsset, setExerciseAsset] =
    React.useState<ExerciseAssetDto | null>(null);
  // state to hold public/team copy of exercise to sync with
  const [exerciseToSyncWith, setExerciseToSyncWith] =
    React.useState<ExerciseAssetDto | null>(null);

  const isAllBodypartsSelected = contextBodyparts === ALL_OPTIONS;

  const isAllMovementPatternsSelected = localMovementPatterns === ALL_OPTIONS;

  const bodyparts = isAllBodypartsSelected ? allBodyparts : contextBodyparts;

  const movementPatterns = isAllMovementPatternsSelected
    ? allMovementPatterns
    : localMovementPatterns;

  const isAllTagsSelected = localStorageTagIds === ALL_OPTIONS;

  const allTagIds = (exerciseTags ?? []).map((tag) => tag.id);

  const tagIds = isAllTagsSelected
    ? [...allTagIds, NONE_TAG_ID]
    : localStorageTagIds;

  const { data: exerciseAssetItems, isFetching } = useQuery({
    queryKey: [
      COACH_EXERCISE_LIBRARY_ASSETS_QUERY_KEY,
      {
        page,
        rowsPerPage,
        query: debounceQuery,
        sortKey,
        bodyparts,
        movementPatterns,
        status,
        exerciseType,
        tagIds: isAllTagsSelected ? null : tagIds,
      },
    ],
    queryFn: () =>
      ExerciseAssetsService.getExerciseAssets(
        page,
        rowsPerPage,
        query,
        status,
        isAllBodypartsSelected ? null : bodyparts,
        isAllMovementPatternsSelected ? null : movementPatterns,
        sortKey,
        exerciseType,
        isAllTagsSelected ? null : tagIds,
      ),
    placeholderData: keepPreviousData,
  });

  const handleChangePage = (_, newPage) => {
    const newPageNumber = newPage + 1;
    setPage(newPageNumber);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(event.target.value);
    setPage(1);
  };

  const handleSelectExercise = (
    exerciseAsset: ExerciseAssetDto,
    publicTeamExercise?: ExerciseAssetDto,
  ) => {
    setExerciseAsset(exerciseAsset);
    publicTeamExercise && setExerciseToSyncWith(publicTeamExercise);
    setExerciseLibraryDrawerOpen(true);
  };

  const handleCloseExerciseDrawer = () => {
    setExerciseLibraryDrawerOpen(false);
    setExerciseAsset(null);
    setExerciseToSyncWith(null);
  };

  const emptyRows = Array.from(new Array(rowsPerPage));

  React.useEffect(() => {
    if (
      exerciseTags &&
      exerciseTags.length > 0 &&
      (!tagIds || tagIds.length === 0)
    ) {
      handleTagIdsChange(null, true);
    }
  }, [exerciseTags, tagIds, handleTagIdsChange]);

  return (
    <Paper className={s.root}>
      <Box className={s.container}>
        <Table className={s.table} aria-label="exercise table">
          {!mdUp ? (
            !isPendingTab && (
              <Box
                display={"flex"}
                justifyContent={"space-between"}
                alignItems={"center"}
                width={"100%"}
                flexWrap={"wrap"}
              >
                <MovementPatternFilter
                  onChange={handleMovementPatternChange}
                  contextFilters={movementPatterns}
                />
                <BodypartFilter
                  onChange={handleBodypartChange}
                  contextFilters={bodyparts}
                />
                {exerciseTags && tagIds ? (
                  <TagsFilters
                    selectedTagIds={tagIds}
                    onTagChange={handleTagIdsChange}
                    tags={exerciseTags}
                  />
                ) : (
                  "Tags"
                )}
              </Box>
            )
          ) : (
            <TableHead className={JourneyAnchors.EXERCISES_TABLE_HEADER}>
              <TableRow>
                <TableCell sx={{ width: columnWidths.thumbnail }}>
                  Video
                </TableCell>
                <TableCell sx={{ width: columnWidths.title }}>
                  <NameSortButton
                    sortKey={sortKey}
                    className={s.nameSortButton}
                    onChangeSortKey={handleSortKeyChange}
                  />
                </TableCell>
                {!isPendingTab && (
                  <>
                    <TableCell
                      align="center"
                      sx={{ width: columnWidths.movementPattern }}
                    >
                      <MovementPatternFilter
                        onChange={handleMovementPatternChange}
                        contextFilters={movementPatterns}
                      />
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{ width: columnWidths.bodypart }}
                    >
                      <BodypartFilter
                        onChange={handleBodypartChange}
                        contextFilters={bodyparts}
                      />
                    </TableCell>
                  </>
                )}
                {!isPendingTab ? (
                  <TableCell align="center" sx={{ width: columnWidths.tags }}>
                    {exerciseTags?.length > 0 && tagIds?.length > 0 ? (
                      <TagsFilters
                        selectedTagIds={tagIds}
                        onTagChange={handleTagIdsChange}
                        tags={exerciseTags}
                      />
                    ) : (
                      "Tags"
                    )}
                  </TableCell>
                ) : (
                  <TableCell sx={{ width: columnWidths.suggestedBy }}>
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="flex-start"
                    >
                      <Button variant="text" className={s.suggestedBy}>
                        Suggested by
                      </Button>
                      <MinimizedTooltip
                        opacity={1}
                        placement="top"
                        title="Client who suggested this exercise"
                      >
                        <Info className={s.infoIcon} />
                      </MinimizedTooltip>
                    </Box>
                  </TableCell>
                )}
                {isArchivedTab && (
                  <TableCell align="center" sx={{ width: columnWidths.images }}>
                    Media
                  </TableCell>
                )}

                {isPublishedTab && (
                  <TableCell align="center" sx={{ width: columnWidths.type }}>
                    Access
                  </TableCell>
                )}
                <TableCell align="center" sx={{ width: columnWidths.actions }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
          )}
          {isFetching ? (
            emptyRows.map((_, index) => (
              <React.Fragment key={index}>
                {mdUp ? (
                  <ExerciseLibraryTableSkeleton
                    className={clsx(s.table)}
                    isPendingTab={isPendingTab}
                    isPublishedTab={isPublishedTab}
                    isArchivedTab={isArchivedTab}
                  />
                ) : (
                  <ExerciseLibraryCardSkeleton />
                )}
              </React.Fragment>
            ))
          ) : (
            <>
              <TableBody>
                {exerciseAssetItems.items.length > 0 ? (
                  exerciseAssetItems.items.map((exercise, index) => (
                    <React.Fragment key={exercise.id}>
                      {mdUp ? (
                        <ExercisesTableRow
                          exerciseInfo={exercise}
                          className={
                            index == 0 && JourneyAnchors.FIRST_EXERCISE
                          }
                          onSelectedExercise={handleSelectExercise}
                          isPendingTab={isPendingTab}
                          isPublishedTab={isPublishedTab}
                          isArchivedTab={isArchivedTab}
                        />
                      ) : (
                        <ExercisesTableMobileView
                          exerciseInfo={exercise}
                          onSelectedExercise={handleSelectExercise}
                        />
                      )}
                    </React.Fragment>
                  ))
                ) : (
                  <TableRow>
                    <TableCell align="center" colSpan={9}>
                      {isPendingTab
                        ? "Only clients can add pending exercises. Don't worry, once they propose a new exercise to your library, we will notify you and you will see them here."
                        : "No exercises found for current filters."}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </>
          )}
          <TableFooter>
            <TableRow>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                colSpan={9}
                count={exerciseAssetItems?.totalCount || 0}
                rowsPerPage={rowsPerPage}
                page={Number(page - 1)}
                showFirstButton
                showLastButton
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </TableRow>
          </TableFooter>
        </Table>
        <EditorProgramContext.Provider value={{ programId: 0 }}>
          {exerciseLibraryDrawerOpen && (
            <ExerciseLibraryDrawer
              exerciseAsset={exerciseAsset}
              onUpdate={() => {
                setExerciseLibraryDrawerOpen(false);
              }}
              onSelectExercise={(exercise, exerciseToSyncWith) => {
                setExerciseAsset(exercise);
                setExerciseToSyncWith(exerciseToSyncWith);
              }}
              exerciseToSyncWith={exerciseToSyncWith}
              onCancel={handleCloseExerciseDrawer}
              onClose={handleCloseExerciseDrawer}
              onRemove={handleCloseExerciseDrawer}
              open={exerciseLibraryDrawerOpen}
              disableChangeExercise={disableChangeExercise}
            />
          )}
        </EditorProgramContext.Provider>
      </Box>
    </Paper>
  );
}
